import { ChangeEvent, ComponentProps, forwardRef, useState } from "react";

import { Input } from "@/components/_atoms/input";
import { parseNumberInput } from "@/lib/parse-number-input";

const DIGITS_ONLY_PATTERN = "[0-9]*";
const SIGNED_DIGITS_ONLY_PATTERN = "-?[0-9]*";
const DECIMAL_COMMA = ",";
const DECIMAL_POINT = ".";

interface NumberInputProps extends Omit<ComponentProps<typeof Input>, "value" | "onChange" | "type"> {
  value: number | null;
  onValueChange: (value: number | null) => void;
  allowDecimals?: boolean;
  allowNegative?: boolean;
  normalizeOnBlur?: boolean;
}

interface DraftOptions {
  allowDecimals: boolean;
  allowNegative: boolean;
}

const formatDraft = (value: number | null, { allowDecimals }: DraftOptions): string => {
  if (value === null || !Number.isFinite(value)) {
    return "";
  }
  const plainDraft = String(value);
  return allowDecimals ? plainDraft.replace(DECIMAL_POINT, DECIMAL_COMMA) : plainDraft;
};

const resolveDraftValue = (draft: string, options: DraftOptions): number | null =>
  parseNumberInput({ rawValue: draft, ...options })?.value ?? null;

const resolveExternalDraft = (value: number | null, options: DraftOptions): string => {
  const formattedDraft = formatDraft(value, options);
  return parseNumberInput({ rawValue: formattedDraft, ...options }) === null ? "" : formattedDraft;
};

interface SyncedInput extends DraftOptions {
  value: number | null;
}

const hasSyncedInputChanged = (synced: SyncedInput, next: SyncedInput): boolean =>
  !Object.is(synced.value, next.value) ||
  synced.allowDecimals !== next.allowDecimals ||
  synced.allowNegative !== next.allowNegative;

const resolveWholeNumberPattern = ({ allowDecimals, allowNegative }: DraftOptions): string | undefined => {
  if (allowDecimals) {
    return undefined;
  }
  return allowNegative ? SIGNED_DIGITS_ONLY_PATTERN : DIGITS_ONLY_PATTERN;
};

const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    { value, onValueChange, allowDecimals = false, allowNegative = false, normalizeOnBlur = false, onBlur, ...props },
    ref,
  ) => {
    const draftOptions: DraftOptions = { allowDecimals, allowNegative };
    const nextSyncedInput: SyncedInput = { value, ...draftOptions };
    const [draft, setDraft] = useState(() => resolveExternalDraft(value, draftOptions));
    const [syncedInput, setSyncedInput] = useState(nextSyncedInput);

    if (hasSyncedInputChanged(syncedInput, nextSyncedInput)) {
      setSyncedInput(nextSyncedInput);
      const hasOnlyValueChanged =
        syncedInput.allowDecimals === allowDecimals && syncedInput.allowNegative === allowNegative;
      if (!hasOnlyValueChanged || resolveDraftValue(draft, draftOptions) !== value) {
        setDraft(resolveExternalDraft(value, draftOptions));
      }
    }

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const parsed = parseNumberInput({ rawValue: event.target.value, ...draftOptions });
      if (parsed === null) {
        return;
      }
      setDraft(parsed.draft);
      onValueChange(parsed.value);
    };

    return (
      <Input
        {...props}
        ref={ref}
        type="text"
        inputMode={allowDecimals ? "decimal" : "numeric"}
        pattern={resolveWholeNumberPattern(draftOptions)}
        value={draft}
        onChange={handleChange}
        onBlur={(event) => {
          if (normalizeOnBlur) setDraft(resolveExternalDraft(value, draftOptions));
          onBlur?.(event);
        }}
      />
    );
  },
);

NumberInput.displayName = "NumberInput";

export { NumberInput };
